import React, { useState, useEffect, useContext } from 'react';
import {
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Box,
  Toolbar,
  TablePagination,
  IconButton,
  Tooltip,
  Avatar,
  TextField,
  InputAdornment,
  CircularProgress
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  FilterList as FilterIcon,
  Sync as SyncIcon,
  Receipt as ReceiptIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import OrderDetailModal from '../../components/OrderDetailModal';
import OrderNotificationModal from '../../components/OrderNotificationModal';
import OrderFilters from '../../components/OrderFilters';
import NappNotConfigured from '../../components/NappNotConfigured';
import useNappStatus from '../../hooks/useNappStatus';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchField: {
    width: 300,
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    background: theme.palette.primary.light,
    color: 'white',
    '&.pending': {
      background: '#ff9800',
    },
    '&.inProgress': {
      background: '#2196f3',
    },
    '&.delivered': {
      background: '#4caf50',
    },
    '&.canceled': {
      background: '#f44336',
    },
  },
  orderCard: {
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  orderCode: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  customerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  platformChip: {
    fontSize: '0.7rem',
    height: 20,
  },
  statusChip: {
    fontWeight: 'bold',
  },
  totalAmount: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  orderActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

// Status configurations
const statusConfig = {
  CREATING: { label: 'Criando', color: 'default' },
  CREATED: { label: 'Criado', color: 'primary' },
  PAYMENT_PENDING: { label: 'Pag. Pendente', color: 'secondary' },
  PAYMENT_APPROVED: { label: 'Pag. Aprovado', color: 'primary' },
  WAITING_CONFIRMATION: { label: 'Aguard. Confirm.', color: 'secondary' },
  CONFIRMED: { label: 'Confirmado', color: 'primary' },
  INVOICED: { label: 'Faturado', color: 'primary' },
  IN_PREPARATION: { label: 'Em Preparação', color: 'secondary' },
  READY_TO_PICKUP: { label: 'Pronto p/ Retirada', color: 'secondary' },
  SHIPMENT: { label: 'Em Transporte', color: 'secondary' },
  DELIVERED: { label: 'Entregue', color: 'primary' },
  CANCELED: { label: 'Cancelado', color: 'default' },
  RETURNED: { label: 'Devolvido', color: 'default' }
};

const OrdersPage = () => {
  const classes = useStyles();
  const nappStatus = useNappStatus();

  // State management
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  // Modals
  const [orderDetailModal, setOrderDetailModal] = useState({ open: false, order: null });
  const [notificationModal, setNotificationModal] = useState({ open: false, order: null });
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Load data
  const loadOrders = async () => {
    try {
      setLoading(true);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (platformFilter) params.platformName = platformFilter;
      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;

      const response = await api.get('/orders', { params });

      setOrders(response.data.orders);
      setTotalCount(response.data.pagination.total);

    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = {};
      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;

      const response = await api.get('/orders/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Effects
  useEffect(() => {
    loadOrders();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, statusFilter, platformFilter, dateFromFilter, dateToFilter]);

  // Handlers
  const handleSync = async () => {
    try {
      setSyncing(true);
      await api.post('/orders/sync');
      toast.success('Sincronização concluída com sucesso!');
      loadOrders();
      loadStats();
    } catch (error) {
      console.error('Erro na sincronização:', error);
      toast.error('Erro na sincronização: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleOrderClick = (order) => {
    setOrderDetailModal({ open: true, order });
  };

  const handleWhatsAppClick = (order) => {
    if (!order.customerPhone) {
      toast.warning('Pedido não possui telefone para WhatsApp');
      return;
    }

    // Open WhatsApp in new tab
    const url = `https://wa.me/55${order.customerPhone.replace(/\D/g, '')}`;
    window.open(url, '_blank');
  };

  const handleSendNotification = (order) => {
    setNotificationModal({ open: true, order });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const getStatusChip = (status) => {
    const config = statusConfig[status] || { label: status, color: 'default' };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        className={classes.statusChip}
      />
    );
  };

  const renderStatsCards = () => {
    if (!stats.byStatus) return null;

    const statsCards = [
      {
        key: 'pending',
        label: 'Pendentes',
        value: ['CREATING', 'CREATED', 'PAYMENT_PENDING', 'WAITING_CONFIRMATION']
          .reduce((sum, status) => sum + (stats.byStatus[status] || 0), 0),
        icon: <ScheduleIcon />
      },
      {
        key: 'inProgress',
        label: 'Em Andamento',
        value: ['PAYMENT_APPROVED', 'CONFIRMED', 'INVOICED', 'IN_PREPARATION', 'READY_TO_PICKUP', 'SHIPMENT']
          .reduce((sum, status) => sum + (stats.byStatus[status] || 0), 0),
        icon: <ShippingIcon />
      },
      {
        key: 'delivered',
        label: 'Entregues',
        value: stats.byStatus.DELIVERED || 0,
        icon: <ReceiptIcon />
      },
      {
        key: 'canceled',
        label: 'Cancelados',
        value: (stats.byStatus.CANCELED || 0) + (stats.byStatus.RETURNED || 0),
        icon: <PaymentIcon />
      }
    ];

    return (
      <Grid container spacing={2} className={classes.statsContainer}>
        {statsCards.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.key}>
            <Card className={`${classes.statCard} ${stat.key}`}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" component="div">
                  {stat.value}
                </Typography>
                <Typography variant="body2">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderOrderCard = (order) => (
    <Card key={order.id} className={classes.orderCard} onClick={() => handleOrderClick(order)}>
      <CardContent>
        <div className={classes.orderHeader}>
          <Typography className={classes.orderCode}>
            #{order.code}
          </Typography>
          {getStatusChip(order.status)}
        </div>

        <div className={classes.customerInfo}>
          <Avatar style={{ width: 32, height: 32 }}>
            {order.customerName.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {order.customerName}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatDate(order.orderDate)}
            </Typography>
          </div>
          <Chip
            label={order.platformName}
            size="small"
            className={classes.platformChip}
            variant="outlined"
          />
        </div>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.totalAmount}>
            {formatCurrency(order.totalAmount)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {order.items?.length || 0} itens
          </Typography>
        </Box>

        <div className={classes.orderActions} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Ver Detalhes">
            <IconButton size="small" onClick={() => handleOrderClick(order)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>

          {order.customerPhone && (
            <Tooltip title="WhatsApp">
              <IconButton size="small" onClick={() => handleWhatsAppClick(order)}>
                <WhatsAppIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Enviar Notificação">
            <IconButton size="small" onClick={() => handleSendNotification(order)}>
              <SendIcon />
            </IconButton>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );

  // Se não tem permissão para NAPP
  if (!nappStatus.hasPermission) {
    return (
      <div className={classes.root}>
        <Paper>
          <Box p={3} textAlign="center">
            <Typography variant="h5" gutterBottom>
              Recurso Não Disponível
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Este recurso não está disponível no seu plano atual.
            </Typography>
          </Box>
        </Paper>
      </div>
    );
  }

  // Se ainda está carregando a verificação
  if (nappStatus.isLoading) {
    return (
      <div className={classes.root}>
        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  // Se não está configurado ou precisa de configuração
  if (!nappStatus.isConfigured || nappStatus.needsConfiguration) {
    return <NappNotConfigured pageType="orders" />;
  }

  return (
    <div className={classes.root}>
      <Paper>
        {/* Header */}
        <Toolbar className={classes.toolbar}>
          <Typography variant="h5" component="h1">
            Pedidos E-commerce
          </Typography>

          <Box display="flex" gap={1}>
            <TextField
              className={classes.searchField}
              placeholder="Buscar por código, cliente, telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />

            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFiltersOpen(true)}
            >
              Filtros
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={syncing ? <SyncIcon /> : <RefreshIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          </Box>
        </Toolbar>

        {/* Stats Cards */}
        {renderStatsCards()}

        {/* Orders List */}
        {loading ? (
          <div className={classes.loadingContainer}>
            <Typography>Carregando pedidos...</Typography>
          </div>
        ) : orders.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6">Nenhum pedido encontrado</Typography>
            <Typography variant="body2">
              Tente ajustar os filtros ou sincronizar com a NAPP
            </Typography>
          </div>
        ) : (
          <div>
            <Grid container spacing={2}>
              {orders.map((order) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
                  {renderOrderCard(order)}
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Pedidos por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
            />
          </div>
        )}
      </Paper>

      {/* Modals */}
      <OrderDetailModal
        open={orderDetailModal.open}
        order={orderDetailModal.order}
        onClose={() => setOrderDetailModal({ open: false, order: null })}
        onOrderUpdate={loadOrders}
      />

      <OrderNotificationModal
        open={notificationModal.open}
        order={notificationModal.order}
        onClose={() => setNotificationModal({ open: false, order: null })}
      />

      <OrderFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={{
          status: statusFilter,
          platform: platformFilter,
          dateFrom: dateFromFilter,
          dateTo: dateToFilter,
        }}
        onFiltersChange={(newFilters) => {
          setStatusFilter(newFilters.status || '');
          setPlatformFilter(newFilters.platform || '');
          setDateFromFilter(newFilters.dateFrom || '');
          setDateToFilter(newFilters.dateTo || '');
        }}
      />
    </div>
  );
};

export default OrdersPage;